import React, { useState, useEffect } from 'react';
import { Box, Card, CardContent, Typography, Chip, Button, CircularProgress, Stepper, Step, StepLabel, Divider, IconButton, Grid, Dialog, DialogTitle, DialogContent, DialogActions, TextField,  } from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Description as DescriptionIcon,
  Add as AddIcon,
  Straighten as MeasureIcon,
  Height as HeightIcon,
  AspectRatio as WidthIcon,
  Receipt as InvoiceIcon,
  Send as SendIcon,
  PictureAsPdf as PdfIcon,
  CheckCircle as CheckIcon,
  LocalShipping as DeliveryIcon,
  Close as CloseIcon,
  Print as PrintIcon,
  Download as DownloadIcon,
  ContentCut as TailorIcon,
  Build as InstallerIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Store as StoreIcon,
  Badge as BadgeIcon,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import { userInfoState } from '../../recoil/atoms';
import { toast } from 'react-toastify';
import client from '../../api/client';
import SendToTailorDialog from '../../components/SendToTailorDialog';
import SendToInstallerDialog from '../../components/SendToInstallerDialog';

const themeColors = {
  dark: '#1a1a2e',
  gold: '#d4af37',
  goldLight: '#f4e4bc',
};

const statusSteps = [
  { key: 'pre_order', label: 'پیش‌سفارش' },
  { key: 'measurement', label: 'اندازه‌گیری' },
  { key: 'invoice_ready', label: 'آماده فاکتور' },
  { key: 'invoiced', label: 'فاکتور شده' },
  { key: 'sewing', label: 'در حال دوخت' },
  { key: 'ready_install', label: 'آماده نصب' },
  { key: 'completed', label: 'تکمیل' },
];

const OrderDetail = () => {
  const [order, setOrder] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [changingStatus, setChangingStatus] = useState(false);
  const [pdfDialogOpen, setPdfDialogOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [tailorDialogOpen, setTailorDialogOpen] = useState(false);
  const [installerDialogOpen, setInstallerDialogOpen] = useState(false);
  const [tailorOrder, setTailorOrder] = useState(null);
  const [tailorPdfUrl, setTailorPdfUrl] = useState(null);
  const [tailorPdfDialogOpen, setTailorPdfDialogOpen] = useState(false);
  
  // ویرایش توضیحات
  const [editingDescription, setEditingDescription] = useState(false);
  const [descriptionText, setDescriptionText] = useState('');
  const [savingDescription, setSavingDescription] = useState(false);
  
  const { id } = useParams();
  const navigate = useNavigate();
  const userInfo = useRecoilValue(userInfoState);
  const isSalesperson = userInfo?.role?.toLowerCase() === 'salesperson';

  useEffect(() => {
    fetchOrder();
  }, [id]);

  const fetchOrder = async () => {
    try {
      const response = await client.get(`/orders/${id}/`);
      setOrder(response.data);
      
      // اگر فاکتور داره، اطلاعات همه فاکتورها رو بگیر
      if (response.data.has_invoice && response.data.invoice_ids?.length > 0) {
        fetchAllInvoices(response.data.invoice_ids);
      } else {
        setInvoices([]);
      }
    } catch (error) {
      toast.error('خطا در دریافت اطلاعات سفارش');
      navigate('/shop/orders');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllInvoices = async (invoiceIds) => {
    try {
      const promises = invoiceIds.map(invoiceId => 
        client.get(`/orders/invoices/${invoiceId}/`)
      );
      const responses = await Promise.all(promises);
      setInvoices(responses.map(r => r.data));
    } catch (error) {
      console.error('Error fetching invoices:', error);
    }
  };

  const fetchInvoice = async (invoiceId) => {
    try {
      const response = await client.get(`/orders/invoices/${invoiceId}/`);
      return response.data;
    } catch (error) {
      console.error('Error fetching invoice:', error);
      return null;
    }
  };

  // ذخیره توضیحات سفارش
  const handleSaveDescription = async () => {
    setSavingDescription(true);
    try {
      await client.put(`/orders/${id}/description/`, { 
        description: descriptionText 
      });
      toast.success('توضیحات ذخیره شد');
      setEditingDescription(false);
      fetchOrder();
    } catch (error) {
      toast.error('خطا در ذخیره توضیحات');
    } finally {
      setSavingDescription(false);
    }
  };

  const startEditDescription = () => {
    setDescriptionText(order.pre_order?.description || '');
    setEditingDescription(true);
  };

  const cancelEditDescription = () => {
    setEditingDescription(false);
    setDescriptionText('');
  };

  // آیا می‌تونه توضیحات رو ویرایش کنه؟
  // فقط وقتی که هنوز اندازه‌گیری ثبت نشده
  const canEditDescription = order?.measurements?.length === 0;

  const handleChangeStatus = async (newStatus) => {
    setChangingStatus(true);
    try {
      await client.put(`/orders/${id}/status/`, { status: newStatus });
      toast.success('وضعیت سفارش تغییر کرد');
      fetchOrder();
    } catch (error) {
      toast.error('خطا در تغییر وضعیت');
    } finally {
      setChangingStatus(false);
    }
  };

  const handleViewPdf = async (invoiceToView) => {
    if (!invoiceToView) return;
    setSelectedInvoice(invoiceToView);
    setPdfLoading(true);
    setPdfDialogOpen(true);
    
    try {
      // از endpoint جدید استفاده میکنیم که JSON با base64 برمیگردونه
      // اینطوری IDM نمیتونه intercept کنه چون response یه JSON عادیه
      const response = await client.get(`/orders/invoices/${invoiceToView.id}/pdf-data/`);
      
      const dataUrl = `data:application/pdf;base64,${response.data.pdf}`;
      setPdfUrl(dataUrl);
    } catch (error) {
      console.error('PDF Error:', error);
      toast.error('خطا در بارگذاری PDF');
      setPdfDialogOpen(false);
    } finally {
      setPdfLoading(false);
    }
  };
  
  const handleClosePdf = () => {
    setPdfDialogOpen(false);
    setPdfUrl(null);
    setSelectedInvoice(null);
  };
  
  const handlePrintPdf = () => {
    if (pdfUrl) {
      // برای پرینت، یه iframe مخفی میسازیم
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.src = pdfUrl;
      document.body.appendChild(iframe);
      iframe.onload = () => {
        iframe.contentWindow.print();
      };
    }
  };
  
  const handleDownloadPdf = () => {
    if (pdfUrl && selectedInvoice) {
      const link = document.createElement('a');
      link.href = pdfUrl;
      link.download = `invoice_${selectedInvoice.invoice_number}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };
  
  // نمایش PDF دستور کار خیاط
  const handleViewTailorPdf = async () => {
    if (!order.tailor_order) return;
    setPdfLoading(true);
    setTailorPdfDialogOpen(true);
    
    try {
      const response = await client.get(`/orders/tailor-orders/${order.tailor_order.id}/pdf/`);
      const dataUrl = `data:application/pdf;base64,${response.data.pdf}`;
      setTailorPdfUrl(dataUrl);
    } catch (error) {
      console.error('Tailor PDF Error:', error);
      toast.error('خطا در بارگذاری PDF');
      setTailorPdfDialogOpen(false);
    } finally {
      setPdfLoading(false);
    }
  };
  
  const handleCloseTailorPdf = () => {
    setTailorPdfDialogOpen(false);
    setTailorPdfUrl(null);
  };
  
  const handlePrintTailorPdf = () => {
    if (tailorPdfUrl) {
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.src = tailorPdfUrl;
      document.body.appendChild(iframe);
      iframe.onload = () => {
        iframe.contentWindow.print();
      };
    }
  };
  
  const handleDownloadTailorPdf = () => {
    if (tailorPdfUrl && order) {
      const link = document.createElement('a');
      link.href = tailorPdfUrl;
      link.download = `work_order_${order.order_number}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };
  
  // لینک PDF برای SMS
  const getPdfShareLink = () => {
    return `${window.location.origin}/api/orders/invoices/${selectedInvoice?.id}/pdf/`;
  };

  const getActiveStep = () => {
    const index = statusSteps.findIndex(s => s.key === order?.status);
    return index >= 0 ? index : 0;
  };

  const toPersianDigits = (str) => {
    const persianDigits = '۰۱۲۳۴۵۶۷۸۹';
    return str?.toString().replace(/[0-9]/g, (d) => persianDigits[d]) || '';
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('fa-IR').format(num || 0);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress sx={{ color: themeColors.gold }} />
      </Box>
    );
  }

  if (!order) return null;

  return (
    <Box sx={{ p: 2, pb: 10 }}>
      {/* هدر */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <IconButton onClick={() => navigate('/shop/orders')}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h6" fontWeight="bold">
          سفارش {toPersianDigits(order.order_number)}
        </Typography>
      </Box>

      {/* وضعیت */}
<Card sx={{ mb: 2 }}>
  <CardContent>
    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
      وضعیت سفارش
    </Typography>
    {/* Desktop: Horizontal Stepper */}
    <Box sx={{ 
      display: { xs: 'none', md: 'block' },
      overflowX: 'auto'
    }}>
      <Stepper activeStep={getActiveStep()} alternativeLabel sx={{ mt: 2, minWidth: 600 }}>
        {statusSteps.map((step) => (
          <Step key={step.key}>
            <StepLabel>{step.label}</StepLabel>
          </Step>
        ))}
      </Stepper>
    </Box>

    {/* Mobile: Vertical Stepper */}
    <Box sx={{ display: { xs: 'block', md: 'none' }, mt: 2 }}>
      <Stepper activeStep={getActiveStep()} orientation="vertical">
        {statusSteps.map((step) => (
          <Step key={step.key}>
            <StepLabel>{step.label}</StepLabel>
          </Step>
        ))}
      </Stepper>
    </Box>
  </CardContent>
</Card>

      {/* اطلاعات مشتری */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            <PersonIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
            اطلاعات مشتری
          </Typography>
          <Divider sx={{ my: 1 }} />
          <Typography variant="body1">{order.customer_name}</Typography>
          <Typography variant="body2" color="text.secondary" dir="ltr" sx={{ textAlign: 'right' }}>
            <PhoneIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
            {toPersianDigits(order.customer_mobile)}
          </Typography>
          
          {/* سفارش گیرنده */}
          <Box sx={{ mt: 2, pt: 1, borderTop: '1px dashed', borderColor: 'divider' }}>
            <Typography variant="body2" color="text.secondary">
              {order.salesperson_name ? (
                <>
                  <BadgeIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
                  سفارش گیرنده: <strong>{order.salesperson_name}</strong>
                </>
              ) : (
                <>
                  <StoreIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
                  سفارش گیرنده: <strong>فروشگاه</strong>
                </>
              )}
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* توضیحات پیش‌سفارش */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              <DescriptionIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
              توضیحات سفارش
            </Typography>
            {canEditDescription && !editingDescription && (
              <IconButton 
                size="small" 
                onClick={startEditDescription}
                sx={{ color: themeColors.gold }}
              >
                <EditIcon />
              </IconButton>
            )}
          </Box>
          <Divider sx={{ my: 1 }} />
          
          {editingDescription ? (
            <Box>
              <TextField
                fullWidth
                multiline
                rows={4}
                value={descriptionText}
                onChange={(e) => setDescriptionText(e.target.value)}
                placeholder="توضیحات سفارش را وارد کنید..."
                variant="outlined"
                sx={{ mb: 2 }}
              />
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<CancelIcon />}
                  onClick={cancelEditDescription}
                  disabled={savingDescription}
                >
                  انصراف
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={savingDescription ? <CircularProgress size={16} /> : <SaveIcon />}
                  onClick={handleSaveDescription}
                  disabled={savingDescription}
                  sx={{
                    bgcolor: themeColors.gold,
                    color: themeColors.dark,
                    '&:hover': { bgcolor: themeColors.goldLight },
                  }}
                >
                  ذخیره
                </Button>
              </Box>
            </Box>
          ) : (
            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
              {order.pre_order?.description || 'توضیحاتی ثبت نشده'}
            </Typography>
          )}
          
          {!canEditDescription && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              * پس از ثبت اندازه‌گیری، امکان ویرایش توضیحات وجود ندارد
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* اندازه‌گیری‌ها */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="subtitle1" fontWeight="bold">
              <MeasureIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
              اندازه‌گیری‌ها ({toPersianDigits(order.measurements_count)})
            </Typography>
            {/* فروشنده نمی‌تونه اندازه‌گیری اضافه کنه */}
            {!isSalesperson && (order.status === 'pre_order' || order.status === 'measurement') && (
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() => navigate(`/shop/orders/${id}/measurement`)}
                sx={{ color: themeColors.gold }}
              >
                افزودن
              </Button>
            )}
          </Box>
          <Divider sx={{ my: 1 }} />
          
          {order.measurements?.length > 0 ? (
            order.measurements.map((m, index) => (
              <Card
                key={m.id}
                variant="outlined"
                sx={{
                  mb: 1.5,
                  bgcolor: 'grey.50',
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Typography variant="subtitle1" fontWeight="bold" color="primary" gutterBottom>
                    {toPersianDigits((index + 1).toString())}. {m.location}
                  </Typography>
                  
                  <Box sx={{ display: 'flex', gap: 3, mb: 1, flexWrap: 'wrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <HeightIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                      <Typography variant="body2">
                        ارتفاع: <strong>{toPersianDigits(m.height)}</strong> cm
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <WidthIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                      <Typography variant="body2">
                        عرض: <strong>{toPersianDigits(m.width)}</strong> cm
                      </Typography>
                    </Box>
                  </Box>

                  {m.fabric_types_names?.length > 0 && (
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                        نوع پارچه:
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {m.fabric_types_names.map((name, idx) => (
                          <Chip 
                            key={idx} 
                            size="small" 
                            label={name}
                            sx={{ 
                              bgcolor: themeColors.gold,
                              color: themeColors.dark,
                              fontWeight: 'bold',
                            }} 
                          />
                        ))}
                      </Box>
                    </Box>
                  )}

                  {m.description && (
                    <Box sx={{ 
                      mt: 1, 
                      pt: 1, 
                      borderTop: '1px dashed',
                      borderColor: 'divider',
                    }}>
                      <Typography variant="caption" color="text.secondary">
                        توضیحات:
                      </Typography>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {m.description}
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            ))
          ) : (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                هنوز اندازه‌گیری ثبت نشده
              </Typography>
              {/* فروشنده نمی‌تونه اندازه‌گیری اضافه کنه */}
              {!isSalesperson && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => navigate(`/shop/orders/${id}/measurement`)}
                  sx={{
                    mt: 1,
                    bgcolor: themeColors.gold,
                    color: themeColors.dark,
                    '&:hover': { bgcolor: themeColors.goldLight },
                  }}
                >
                  ثبت اندازه‌گیری
                </Button>
              )}
              {isSalesperson && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  * ثبت اندازه‌گیری توسط فروشگاه انجام می‌شود
                </Typography>
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* فاکتورها - نمایش بعد از ثبت */}
      {order.has_invoice && invoices.length > 0 && (
        <Card sx={{ mb: 2, border: `2px solid ${themeColors.gold}` }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle1" fontWeight="bold" color={themeColors.gold}>
                <InvoiceIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                فاکتورها ({toPersianDigits(invoices.length.toString())})
              </Typography>
              {/* دکمه افزودن فاکتور جدید - فقط تا قبل از خیاطی */}
              {(order.status === 'invoice_ready' || order.status === 'invoiced') && (
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => navigate(`/shop/orders/${id}/invoice`)}
                  sx={{ color: themeColors.gold }}
                >
                  فاکتور جدید
                </Button>
              )}
            </Box>
            <Divider sx={{ my: 1 }} />
            
            {invoices.map((inv, invIndex) => (
              <Box key={inv.id}>
                {invIndex > 0 && <Divider sx={{ my: 2 }} />}
                
                {/* هدر فاکتور */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, mt: invIndex > 0 ? 1 : 0 }}>
                  <Typography variant="body1" fontWeight="bold" color={themeColors.dark}>
                    فاکتور {toPersianDigits(inv.invoice_number)}
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<PdfIcon />}
                    onClick={() => handleViewPdf(inv)}
                    sx={{ color: themeColors.gold }}
                  >
                    مشاهده PDF
                  </Button>
                </Box>
                
                {/* اقلام فاکتور */}
                {inv.items?.map((item, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      py: 1,
                      borderBottom: index < inv.items.length - 1 ? '1px solid' : 'none',
                      borderColor: 'divider',
                    }}
                  >
                    <Box>
                      <Typography variant="body2" fontWeight="bold">
                        {item.item_name}
                        {item.description && ` - ${item.description}`}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {toPersianDigits(item.quantity)} {item.unit_type === 'meter' ? 'متر' : 'بار'} × {formatNumber(item.unit_price)} ریال
                      </Typography>
                    </Box>
                    <Typography variant="body2" fontWeight="bold">
                      {formatNumber(item.total_price)} ریال
                    </Typography>
                  </Box>
                ))}
                
                {/* جمع فاکتور */}
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  mt: 1, 
                  pt: 1,
                  borderTop: '1px solid',
                  borderColor: themeColors.gold,
                }}>
                  <Typography variant="body1" fontWeight="bold">جمع این فاکتور:</Typography>
                  <Typography variant="body1" fontWeight="bold" color={themeColors.gold}>
                    {formatNumber(inv.total_amount)} ریال
                  </Typography>
                </Box>
              </Box>
            ))}
            
            {/* جمع کل همه فاکتورها */}
            {invoices.length > 1 && (
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                mt: 2, 
                pt: 2,
                borderTop: '3px solid',
                borderColor: themeColors.gold,
                bgcolor: themeColors.goldLight,
                mx: -2,
                px: 2,
                pb: 0,
                mb: -2,
                borderRadius: '0 0 4px 4px',
              }}>
                <Typography variant="h6" fontWeight="bold">جمع کل فاکتورها:</Typography>
                <Typography variant="h6" fontWeight="bold" color={themeColors.dark}>
                  {formatNumber(invoices.reduce((sum, inv) => sum + (parseFloat(inv.total_amount) || 0), 0))} ریال
                </Typography>
              </Box>
            )}
            {invoices.length === 1 && (
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                mt: 2, 
                pt: 2,
                borderTop: '2px solid',
                borderColor: themeColors.gold,
              }}>
                <Typography variant="h6" fontWeight="bold">جمع کل:</Typography>
                <Typography variant="h6" fontWeight="bold" color={themeColors.gold}>
                  {formatNumber(invoices[0].total_amount)} ریال
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* اطلاعات خیاط - نمایش بعد از ارسال به خیاط */}
      {order.tailor_order && (
        <Card sx={{ mb: 2, border: `2px solid #2196f3` }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="subtitle1" fontWeight="bold" color="#2196f3">
                  <TailorIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                  خیاط: {order.tailor_order.tailor_name}
                </Typography>
                <Typography variant="body2" color="text.secondary" dir="ltr">
                  {toPersianDigits(order.tailor_order.tailor_mobile)}
                </Typography>
              </Box>
              <Button
                size="small"
                startIcon={<PdfIcon />}
                onClick={handleViewTailorPdf}
                sx={{ color: '#2196f3' }}
              >
                مشاهده PDF
              </Button>
            </Box>
            <Divider sx={{ my: 1 }} />
            
            {/* دستور کارها */}
            {order.tailor_order.work_orders?.map((wo, index) => (
              <Box
                key={wo.id}
                sx={{
                  py: 1,
                  borderBottom: index < order.tailor_order.work_orders.length - 1 ? '1px solid' : 'none',
                  borderColor: 'divider',
                }}
              >
                <Typography variant="body2" fontWeight="bold" color="#2196f3">
                  {toPersianDigits((index + 1).toString())}. {wo.location}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, bgcolor: 'grey.50', p: 1, borderRadius: 1 }}>
                  {wo.instructions}
                </Typography>
              </Box>
            ))}
            
            {order.tailor_order.notes && (
              <Box sx={{ mt: 1, p: 1, bgcolor: '#e3f2fd', borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary">توضیحات کلی:</Typography>
                <Typography variant="body2">{order.tailor_order.notes}</Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* اطلاعات نصاب - نمایش بعد از انتخاب نصاب */}
      {order.installer_order && (
        <Card sx={{ mb: 2, border: `2px solid #ff9800` }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="subtitle1" fontWeight="bold" color="#ff9800">
                <InstallerIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                نصاب: {order.installer_order.installer_name}
              </Typography>
              <Typography variant="body2" color="text.secondary" dir="ltr">
                {toPersianDigits(order.installer_order.installer_mobile)}
              </Typography>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* دکمه‌های اکشن بر اساس وضعیت */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {/* دکمه تکمیل اندازه‌گیری و آماده فاکتور */}
        {order.status === 'measurement' && order.measurements?.length > 0 && (
          <Button
            fullWidth
            variant="contained"
            size="large"
            startIcon={changingStatus ? <CircularProgress size={20} color="inherit" /> : <CheckIcon />}
            onClick={() => handleChangeStatus('invoice_ready')}
            disabled={changingStatus}
            sx={{
              py: 1.5,
              bgcolor: '#4caf50',
              color: 'white',
              '&:hover': { bgcolor: '#388e3c' },
            }}
          >
            تکمیل اندازه‌گیری - آماده فاکتور
          </Button>
        )}

        {/* دکمه ایجاد فاکتور */}
        {(order.status === 'invoice_ready' || (order.status === 'invoiced' && !isSalesperson)) && (
          <Button
            fullWidth
            variant="contained"
            size="large"
            startIcon={<InvoiceIcon />}
            onClick={() => navigate(`/shop/orders/${id}/invoice`)}
            sx={{
              py: 1.5,
              bgcolor: themeColors.gold,
              color: themeColors.dark,
              '&:hover': { bgcolor: themeColors.goldLight },
            }}
          >
            {order.has_invoice ? 'افزودن فاکتور جدید' : 'ایجاد فاکتور'}
          </Button>
        )}

        {/* دکمه ارسال به خیاط */}
        {order.status === 'invoiced' && (
          <Button
            fullWidth
            variant="contained"
            size="large"
            startIcon={<TailorIcon />}
            onClick={() => setTailorDialogOpen(true)}
            sx={{
              py: 1.5,
              bgcolor: '#2196f3',
              color: 'white',
              '&:hover': { bgcolor: '#1976d2' },
            }}
          >
            ارسال به خیاط
          </Button>
        )}

        {/* دکمه اتمام دوخت - انتخاب نصاب */}
        {order.status === 'sewing' && (
          <Button
            fullWidth
            variant="contained"
            size="large"
            startIcon={<CheckIcon />}
            onClick={() => setInstallerDialogOpen(true)}
            sx={{
              py: 1.5,
              bgcolor: '#ff9800',
              color: 'white',
              '&:hover': { bgcolor: '#f57c00' },
            }}
          >
            اتمام دوخت - انتخاب نصاب
          </Button>
        )}

        {/* دکمه تکمیل سفارش */}
        {order.status === 'ready_install' && (
          <Button
            fullWidth
            variant="contained"
            size="large"
            startIcon={changingStatus ? <CircularProgress size={20} color="inherit" /> : <CheckIcon />}
            onClick={() => handleChangeStatus('completed')}
            disabled={changingStatus}
            sx={{
              py: 1.5,
              bgcolor: '#4caf50',
              color: 'white',
              '&:hover': { bgcolor: '#388e3c' },
            }}
          >
            نصب شد - تکمیل سفارش
          </Button>
        )}

        {/* نمایش تکمیل شده */}
        {order.status === 'completed' && (
          <Card sx={{ bgcolor: '#e8f5e9', border: '2px solid #4caf50' }}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <CheckIcon sx={{ fontSize: 48, color: '#4caf50', mb: 1 }} />
              <Typography variant="h6" color="#2e7d32" fontWeight="bold">
                سفارش تکمیل شده است
              </Typography>
            </CardContent>
          </Card>
        )}
      </Box>

      {/* PDF Dialog */}
      <Dialog
        open={pdfDialogOpen}
        onClose={handleClosePdf}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            height: '90vh',
            maxHeight: '90vh',
          }
        }}
      >
        <DialogTitle
          sx={{
            bgcolor: themeColors.dark,
            color: themeColors.gold,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            py: 1.5,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PdfIcon />
            <Typography variant="h6">
              فاکتور {selectedInvoice && toPersianDigits(selectedInvoice.invoice_number)}
            </Typography>
          </Box>
          <Box>
            <IconButton onClick={handlePrintPdf} sx={{ color: themeColors.gold }} title="پرینت">
              <PrintIcon />
            </IconButton>
            <IconButton onClick={handleDownloadPdf} sx={{ color: themeColors.gold }} title="دانلود">
              <DownloadIcon />
            </IconButton>
            <IconButton onClick={handleClosePdf} sx={{ color: themeColors.gold }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
          {pdfLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
              <CircularProgress sx={{ color: themeColors.gold }} />
            </Box>
          ) : pdfUrl ? (
            <embed
              src={pdfUrl}
              type="application/pdf"
              width="100%"
              height="100%"
              style={{ flex: 1, border: 'none' }}
            />
          ) : null}
        </DialogContent>
      </Dialog>

      {/* PDF Dialog دستور کار خیاط */}
      <Dialog
        open={tailorPdfDialogOpen}
        onClose={handleCloseTailorPdf}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { height: '90vh', maxHeight: '90vh' } }}
      >
        <DialogTitle
          sx={{
            bgcolor: '#2196f3',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            py: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TailorIcon />
            <Typography variant="h6">دستور کار خیاط</Typography>
          </Box>
          <Box>
            <IconButton onClick={handlePrintTailorPdf} sx={{ color: 'white' }}>
              <PrintIcon />
            </IconButton>
            <IconButton onClick={handleDownloadTailorPdf} sx={{ color: 'white' }}>
              <DownloadIcon />
            </IconButton>
            <IconButton onClick={handleCloseTailorPdf} sx={{ color: 'white' }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
          {pdfLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
              <CircularProgress sx={{ color: '#2196f3' }} />
            </Box>
          ) : tailorPdfUrl ? (
            <embed
              src={tailorPdfUrl}
              type="application/pdf"
              width="100%"
              height="100%"
              style={{ flex: 1, border: 'none' }}
            />
          ) : null}
        </DialogContent>
      </Dialog>

      {/* دیالوگ ارسال به خیاط */}
      <SendToTailorDialog
        open={tailorDialogOpen}
        onClose={() => setTailorDialogOpen(false)}
        order={order}
        measurements={order?.measurements}
        onSuccess={() => {
          fetchOrder();
          setTailorDialogOpen(false);
        }}
      />

      {/* دیالوگ انتخاب نصاب */}
      <SendToInstallerDialog
        open={installerDialogOpen}
        onClose={() => setInstallerDialogOpen(false)}
        order={order}
        onSuccess={() => {
          fetchOrder();
          setInstallerDialogOpen(false);
        }}
      />
    </Box>
  );
};

export default OrderDetail;